/**
 * Surface programs. Each entry is a GLSL fragment defining
 *
 *   void surface(vec2 uv, out Surf s)
 *
 * which is compiled into the pass-1 shader (see `passes.glsl.js`). Everything a
 * surface writes is 0..1:
 *
 *   s.albedo  perceptual / sRGB-ish colour (converted to linear by pass 1)
 *   s.height  drives both the normal map and the cavity/curvature system
 *   s.rough   linear roughness
 *   s.metal   0 for dielectrics, 1 for bare metal
 *   s.ao      baked micro-occlusion the height field cannot express
 *   s.grime   where dirt is *allowed* to collect (pass 2 puts it in the cavities)
 *
 * Authoring notes
 * ---------------
 * - Dielectric albedo is kept inside 0.19..0.91 in this perceptual space, which
 *   is 0.03..0.8 linear — the physically plausible band. Nothing is pure black
 *   or pure white.
 * - Metals write `s.metal = 1` and a *bright* base colour: in a metalness
 *   workflow the base colour of a metal is its F0 reflectance (steel ~0.56,
 *   zinc ~0.65), not a diffuse albedo. Dark metals (parkerised gun steel) are
 *   dark because they are rough, not because F0 is low.
 * - A *coated* metal is the exception, and writes a fractional metalness. That is
 *   not a fudge: weathered galvanising carries a zinc-carbonate patina and a
 *   painted panel carries enamel, and both are real dielectric layers over the
 *   metal. It matters more than it sounds. At metalness 1 the diffuse term
 *   vanishes, so all surface shape has to be carried by the specular lobe — and a
 *   specular-only feature is present or absent depending on where the mirror
 *   direction happens to point, which is why `corrugated` used to read as flat
 *   plywood head-on and as clipped white stripes from anywhere else.
 * - Detail is layered macro -> meso -> micro so the surface holds up both at
 *   3 m and at 20 m. The micro octaves are what mipmapping eats first, which is
 *   exactly what you want.
 */

/* ------------------------------------------------------------------ rust ---
 * Shared corrosion model, prepended to the two steel surfaces.
 *
 * The failure mode this replaces: one mid-frequency noise thresholded hard and
 * lerped between two saturated colours. At the level's 2 m texel density that
 * puts every rust feature at the same ~30 cm size with the same hard edge, and
 * a field of same-sized hard-edged blobs in two saturated colours is, visually,
 * camouflage. It does not matter how good the noise is.
 *
 * Real corrosion is a *history*, and it has three separate scales that are not
 * peers:
 *
 *   region  ~1-2 m   where the coating has failed and where water actually sits
 *   weep    directional — every region bleeds downhill, because gravity exists
 *   bloom   ~5 cm    the pitted front the corrosion is currently advancing along
 *
 * The bloom is *gated* by the region rather than summed with it. That is the
 * whole trick: gating gives "a few corroded areas, with structure inside them",
 * which is what corrosion looks like; summing gives an all-over pattern.
 *
 * Colour is then a four-stop ramp driven by one continuous scalar, so a surface
 * physically cannot jump from paint to oxide — it always passes through chalked
 * coating and light surface bloom on the way. And every stop is *desaturated*:
 * iron oxide on a wall is a dull ochre-brown. The saturated brick-red that
 * "rust = red" produces is the single loudest tell of a procedural material.
 */
const RUST_GLSL = /* glsl */ `
/**
 * One warp, shared by the region and the bloom. Warping at ~50 cm makes the
 * fine bloom cells wander in clumps instead of sitting on their own lattice,
 * which is most of what the bloom needed a warp of its own for.
 */
vec2 rustWarp(vec2 uv, float seed) { return warp(uv, 2.0, 0.16, seed + 7.0); }

/** Corrosion potential. Coarse only — the weep smear eats anything finer. */
float rustSrc(vec2 p, float seed) { return fbmP(p * 2.0, 2.0, 3, seed); }

/**
 * Region + gravity, over the pre-warped point \`p\`. Returns the potential at p
 * *including* oxide that has run down out of sources above it; \`len\` is the run
 * length in uv units.
 *
 * +v is up on every wall in the level, so "above" is +y: sampling the source at
 * +y and weighting it down is a three-tap directional blur, and it is the whole
 * difference between rust that sits on a wall and rust that ran down one.
 */
float rustField(vec2 p, vec2 uv, float seed, float len) {
  // Contrast on the region itself, so the corroded zones are a *minority* of the
  // wall with a wide soft margin, rather than fbm's timid ±0.1 around mid grey.
  // Contrast, but only a little: this field is ~1 m across on a 2 m tile, so any
  // hard threshold on it prints one high-contrast signature blob per tile and a
  // 20 m wall becomes countable. Keep the field soft and let the ramp, the chips
  // and the gating supply the detail.
  float here = sat((rustSrc(p, seed) * 0.72
                  + fbmP(p * 6.0, 6.0, 2, seed + 41.0) * 0.28 - 0.5) * 1.7 + 0.5);

  float run = 0.0, w = 0.0;
  for (int i = 1; i <= 3; i++) {
    float t = float(i) / 3.0;
    float a = 1.0 - t * 0.7;
    run += rustSrc(p + vec2(0.0, t * len), seed) * a;
    w += a;
  }
  run /= w;

  // Rivulets are narrow. A strongly y-elongated noise decides which channels
  // actually carry oxide down the wall and which stay dry — without it the weep
  // is a soft gradient and reads as a smudged lens, not as water paths.
  // (Deliberately *not* streak(): its 0.35-cell tap spacing stair-steps into
  // visible horizontal dashes once you threshold it this hard.)
  float riv = fbm(uv * vec2(200.0, 4.0), vec2(200.0, 4.0), 2, seed + 113.0);
  float weep = sat((run - 0.50) * 2.6) * sat(riv * 2.0 - 0.66);

  return sat(here + weep * 0.45);
}

/** The fine pitted front corrosion advances along. Gate this with rustField. */
float rustBloom(vec2 p, float seed) { return ridged(p * 26.0, 26.0, 4, seed + 17.0); }

/**
 * Four-stop corrosion ramp over one continuous scalar.
 *   0.00  intact coating   (whatever the surface hands in)
 *   0.28  chalked coating  — UV-bleached: desaturated, a little lighter
 *   0.65  surface bloom    — thin ochre film, the coating still legible under it
 *   1.00  scaled oxide     — dark red-brown, flat and matte
 * The stops overlap, so there is never a visible boundary between them, and no
 * pixel can reach oxide without passing through the two intermediate states.
 */
vec3 rustRamp(vec3 coat, float t) {
  vec3 chalk = mix(coat, vec3(0.415, 0.408, 0.392), 0.45);
  vec3 bloom = vec3(0.395, 0.296, 0.216);
  vec3 scale = vec3(0.205, 0.140, 0.104);
  vec3 c = mix(coat, chalk, smoothstep(0.02, 0.34, t));
  c = mix(c, bloom, smoothstep(0.28, 0.66, t));
  c = mix(c, scale, smoothstep(0.60, 1.00, t));
  return c;
}
`;

export const SURFACE_GLSL = {
  /* ------------------------------------------------------------ concrete -- */
  concrete: /* glsl */ `
void surface(vec2 uv, out Surf s) {
  vec2 w1 = warp(uv, 3.0, 0.05, 17.0);

  float macro = fbmP(w1 * 3.0, 3.0, 5, 3.0);        // pour patches / form marks
  float meso  = fbmP(w1 * 11.0, 11.0, 4, 91.0);     // trowel mottling
  float grit  = fbm(uv * 150.0, 150.0, 3, 11.0);    // sand in the cement matrix
  float micro = fbm(uv * 470.0, 470.0, 2, 211.0);   // camera-close grain

  // Aggregate sitting just under the skin of the pour. Two sizes, because real
  // concrete is graded: a few big stones and a lot of small ones.
  vec3 aggA = worley(w1 * 17.0, 17.0, 7.0);
  vec3 aggB = worley(w1 * 38.0, 38.0, 19.0);
  float pebA = smoothstep(0.40, 0.05, aggA.x);
  float pebB = smoothstep(0.34, 0.04, aggB.x);
  float skin = smoothstep(0.35, 0.80, macro);                    // where the cement laitance
  float exposed = sat(pebA * skin * 1.1 + pebB * skin * 0.6);     // wore off the aggregate

  // Trapped air bubbles: sparse, and every one a different size — an even dot
  // field is instantly readable as a repeat once the texture is tiled 24x.
  vec3 bub = worley(warp(uv, 9.0, 0.05, 57.0) * 44.0, 44.0, 53.0);
  float pit = smoothstep(0.17 * (bub.z * bub.z), 0.0, bub.x)
            * smoothstep(0.55, 0.75, bub.z);

  // A *few* structural cracks, warped so they wander instead of following the
  // lattice. Concrete is not crazed all over — that reads as dried mud.
  vec2 cuv = warp(uv, 5.0, 0.22, 71.0);
  vec3 cr = worley(cuv * 5.0, 5.0, 41.0);
  float band  = smoothstep(0.60, 0.86, fbmP(uv * 2.0, 2.0, 3, 101.0));
  float crack = (1.0 - smoothstep(0.0, 0.022, cr.y - cr.x)) * band;

  s.height = 0.56
    + exposed * 0.13 + pebB * 0.04
    + (meso  - 0.5) * 0.16
    + (macro - 0.5) * 0.26
    + (grit  - 0.5) * 0.05
    + (micro - 0.5) * 0.022
    - pit * 0.30
    - crack * 0.38;

  // 0.52 was 0.20 linear — an honest number for a fresh pour, but kerb tops and
  // stair nosings take the low sun at close to normal incidence while the walls
  // take it at a graze, so the horizontal faces were clipping and losing every bit
  // of the microtexture below. 0.495 is 0.176 linear, still squarely inside the
  // band for weathered grey concrete, and it buys back about a third of a stop
  // exactly where it was being lost. The noise structure below is untouched.
  float base = 0.495 + (macro - 0.5) * 0.115 + (meso - 0.5) * 0.085 + (grit - 0.5) * 0.048;
  vec3 col = vec3(base, base * 0.995, base * 0.965);           // cement is faintly warm
  // Exposed stone: greyer, denser, a touch warmer than the matrix around it.
  col = mix(col, vec3(0.50, 0.485, 0.455) * (0.80 + aggA.z * 0.45), exposed * 0.55);
  col = mix(col, col * vec3(1.05, 1.01, 0.95), pebB * skin * 0.3);
  col *= 1.0 - crack * 0.28;

  // A little grime straight onto the albedo as well as into the crevices —
  // a concrete wall is never one flat tone.
  float gm = grimeMask(uv, 5.0);
  col *= 1.0 - gm * 0.14;

  s.albedo = col;
  s.rough  = 0.90 - exposed * 0.14 + (grit - 0.5) * 0.08 + (micro - 0.5) * 0.04 + gm * 0.04;
  s.metal  = 0.0;
  s.ao     = 1.0 - pit * 0.28;
  s.grime  = gm;
}
`,

  /* ------------------------------------------------- painted / rusted steel */
  metalPainted: RUST_GLSL + /* glsl */ `
void surface(vec2 uv, out Surf s) {
  // Roller lay-off marks. 300 cycles into a 1024 texel tile is 3.4 px a cell,
  // which is where valueNoise's axis-aligned lattice starts showing through as
  // a crosshatch weave — so this sits an octave lower than it used to.
  float brush  = fbm(uv * vec2(170.0, 14.0), vec2(170.0, 14.0), 2, 21.0);
  float panel  = fbmP(uv * 4.0, 4.0, 4, 33.0);
  float micro  = fbm(uv * 430.0, 430.0, 2, 133.0);
  float peel   = fbmP(uv * 80.0, 80.0, 2, 45.0);      // roller "orange peel"

  // Where the coating has failed, plus everything that has run down out of it.
  vec2 rp = rustWarp(uv, 61.0);
  float field = rustField(rp, uv, 61.0, 0.45);
  float bloom = rustBloom(rp, 71.0);

  // Paint fails by *chips lifting off*, not by crazing into a net: the giveaway
  // of the previous version was a worley web that read as reptile skin. So the
  // cell id picks which whole flakes have gone, thresholded by how corroded the
  // region under them is — discrete chips, continuously driven.
  vec3 flake = worley(warp(uv, 8.0, 0.05, 71.0) * 30.0, 30.0, 77.0);
  float lift = sat(field * 1.9 - 0.72);
  float chip = smoothstep(lift + 0.06, lift - 0.06, flake.z);
  // Only the *recently* lost chips still have a paint lip around them. Drawing
  // every cell border inside the corroded zone is what turned this into a
  // turtle shell — an old chip has long since weathered its own edge away.
  float fresh = 1.0 - sat((lift - flake.z) * 5.0);
  float flakeEdge = (1.0 - smoothstep(0.0, 0.040, flake.y - flake.x)) * chip * fresh;
  // Scratches down to bare steel: the other classic initiation site.
  float scratch = sat((fbm(uv * vec2(20.0, 190.0), vec2(20.0, 190.0), 2, 87.0) - 0.70) * 4.0);

  // ONE continuous corrosion scalar. Everything below is a function of it, which
  // is what forces the surface through the intermediate states.
  float rust = sat(
      (field - 0.47) * 1.6
    + (bloom - 0.5) * 0.90 * smoothstep(0.32, 0.60, field)   // fine front, gated
    + chip * 0.38
    + scratch * smoothstep(0.26, 0.58, field) * 0.5);

  // Even intact coating is not uniform: UV chalking runs across whole panels,
  // so the wall reads as weathered long before any rust appears on it.
  float chalk = sat(panel * 1.6 - 0.60) * 0.22;
  float t = sat(rust + chalk * (1.0 - rust));

  // Sun-faded industrial grey-green. Light and near-neutral on purpose — the
  // coating has to recede behind the corrosion rather than compete with it.
  float fade = panel * 0.34 + peel * 0.12;
  vec3 paintCol = vec3(0.288 + fade * 0.12, 0.322 + fade * 0.12, 0.305 + fade * 0.105);

  vec3 col = rustRamp(paintCol, t);
  // Scaled oxide is not a flat brown: it is pitted, and the pits are darker and
  // cooler than the plates between them. Reuse the bloom so the pitting lines up
  // with the front that made it.
  col *= 1.0 - smoothstep(0.55, 1.0, t) * (0.62 - bloom) * 0.55;
  col *= 0.96 + (brush - 0.5) * 0.09 + (micro - 0.5) * 0.05;

  s.albedo = col;
  s.height = 0.64
    + (brush - 0.5) * 0.05
    + (panel - 0.5) * 0.10
    + (micro - 0.5) * 0.04
    + peel * 0.05 * (1.0 - t)
    // Oxide jacks the coating up as it forms, then spalls: proud through the
    // mid-ramp, pitted once it has scaled off.
    + smoothstep(0.30, 0.70, t) * 0.055
    - smoothstep(0.66, 1.00, t) * 0.17
    - flakeEdge * smoothstep(0.45, 0.85, t) * 0.14
    - scratch * 0.04;
  s.metal = 0.0;                     // paint and oxide are both dielectric
  // Weathered enamel is matte. At 0.42 the panel held a broad specular sheet
  // under a key light and read as galvanised sheet, not paint — the albedo was
  // never the problem, the gloss was.
  s.rough = mix(0.58 + peel * 0.10, 0.93, smoothstep(0.03, 0.72, t))
          + (brush - 0.5) * 0.08;
  s.ao    = 1.0 - flakeEdge * smoothstep(0.45, 0.9, t) * 0.30;
  s.grime = sat(grimeMask(uv, 91.0) * 0.75 + smoothstep(0.30, 0.85, t) * 0.35);
}
`,

  /* -------------------------------------------------- corrugated galv steel */
  /**
   * Two things about this surface are not like the others, and both follow from
   * one number: its tile is **0.51 m** (6 ribs x 85 mm pitch).
   *
   * 1. Nothing large may be authored with 2-D noise. A 1 m rust region printed
   *    with fbm repeats twice a metre, and a field of same-sized blotches in two
   *    saturated colours is camouflage, not corrosion. Everything at region scale
   *    here is therefore **column-locked** (`fbm1` across u, `drift` down v):
   *    constant or near-constant along the rib, so it tiles invisibly however
   *    short the tile is, and reads as oxide running the full height of the wall.
   *    That is also what real corrugated does — water leaves by the valleys.
   *
   * 2. The rib cannot be a *specular* feature. At metalness 0.82 the diffuse term
   *    is 18% of a mid albedo, so the rib shape was carried almost entirely by the
   *    reflection: looking square at a wall the sun was nowhere near the mirror
   *    direction and the sheet read as flat plywood, while at other angles one web
   *    caught the whole lobe and clipped to white. Detail that inverts with
   *    viewing angle inverts with distance too, because distance changes which
   *    part of the wall is at which angle.
   *
   *    The fix is physical rather than cosmetic. Weathered galvanising is not bare
   *    metal: it carries a zinc-carbonate patina, a genuine mineral film, which is
   *    why old corrugated reads chalky grey rather than mirror-like. Modelled as
   *    the dielectric it is (metalness ~0.4, falling to nothing under oxide), the
   *    rib becomes a *diffuse* light/dark banding — legible from any angle, and it
   *    mips down to a smooth grey instead of a moire of clipped highlights.
   */
  corrugated: /* glsl */ `
/**
 * Galvanised corrosion ramp. Distinct from the painted-steel one because a zinc
 * coating fails differently: it chalks to a dull white carbonate long before any
 * iron shows, and the first iron that does show is a light **ochre** bloom.
 *   0.00  intact zinc
 *   0.26  white chalking  — the carbonate film, near-neutral and slightly light
 *   0.62  ochre bloom     — first iron oxide, yellow-brown
 *   1.00  scaled oxide    — dark red-brown, matte
 * The ochre stop is deliberately far more yellow than brick clay (R/G 1.34 here
 * against 2.14 for the clay in 'brick'), so a rusted fence in front of a brick
 * wall separates instead of melting into one red mass.
 */
vec3 galvRamp(vec3 zinc, float t) {
  vec3 chalk = mix(zinc, vec3(0.428, 0.425, 0.412), 0.55);
  vec3 ochre = vec3(0.345, 0.258, 0.142);
  vec3 oxide = vec3(0.208, 0.139, 0.098);
  vec3 c = mix(zinc, chalk, smoothstep(0.02, 0.30, t));
  c = mix(c, ochre, smoothstep(0.24, 0.62, t));
  c = mix(c, oxide, smoothstep(0.58, 1.00, t));
  return c;
}

void surface(vec2 uv, out Surf s) {
  float RIBS = 6.0;

  // ---- roll-formed profile -------------------------------------------------
  // A trapezoid, not a sine. Real sheet has flat crests and flat valleys joined
  // by short steep webs, and the flats are most of the area — which is what lets
  // the crest be tuned as its own material (most sun, most chalking) instead of
  // being one point on a curve.
  float sx     = fract(uv.x * RIBS);
  float d      = abs(sx - 0.5) * 2.0;              // 0 at crest centre, 1 at valley
  float crest  = smoothstep(0.60, 0.28, d);        // crest flat
  float valley = smoothstep(0.66, 0.92, d);        // valley flat
  float web    = crest * (1.0 - crest) * 4.0;      // the steep transition

  // ---- galvanising ---------------------------------------------------------
  // Spangle: big flat zinc crystals with visible grain boundaries.
  vec3 sp = worley(warp(uv, 5.0, 0.06, 909.0) * 9.0, 9.0, 303.0);
  float spangle = smoothstep(0.05, 0.55, sp.x);
  float spEdge  = 1.0 - smoothstep(0.0, 0.035, sp.y - sp.x);

  // ---- near-field detail ---------------------------------------------------
  // Everything here is fine enough that mipmapping eats it within a few metres,
  // which is the point: it is what appears as the player walks up to the sheet.
  float roll   = fbm(uv * vec2(300.0, 5.0), vec2(300.0, 5.0), 2, 55.0);   // roll-forming lines
  float scrat  = fbm(uv * vec2(150.0, 11.0), vec2(150.0, 11.0), 2, 63.0); // handling scratches
  float micro  = fbm(uv * 430.0, 430.0, 2, 155.0);
  float pit    = smoothstep(0.74, 0.96, fbm(uv * 210.0, 210.0, 2, 167.0)); // corrosion pitting
  // Oil-canning: the gentle dishing a thin sheet takes between its fixings.
  float oilcan = drift(uv, 3.0, 3, 77.0);

  // ---- fasteners -----------------------------------------------------------
  // Two rows a tile is 255 mm, which would be an absurd fixing density, so a
  // per-cell hash keeps roughly one crest in three — about 340 x 510 mm, and
  // irregular, which is what stops the heads reading as a printed grid.
  vec2 scell = vec2(floor(uv.x * RIBS), floor(uv.y * 2.0));
  float has  = step(0.66, cellRand(scell, vec2(RIBS, 2.0), 511.0));
  vec2 sf = vec2(fract(uv.x * RIBS) - 0.5, fract(uv.y * 2.0) - 0.5);
  vec2 sd = sf * vec2(1.0 / RIBS, 0.5);
  float sr = length(sd);
  float head   = smoothstep(0.017, 0.012, sr) * has * crest;
  float washer = smoothstep(0.026, 0.021, sr) * smoothstep(0.015, 0.019, sr) * has * crest;
  float slot   = smoothstep(0.0045, 0.0018, abs(sd.y)) * smoothstep(0.014, 0.010, sr) * has * crest;
  // How far below a fastener we are, 0..1 — the oxide bleed runs downhill from
  // every pierced hole, which is the other place galvanising always fails first.
  float below  = sat((0.5 - fract(uv.y * 2.0)) * 2.2) * smoothstep(0.10, 0.02, abs(sd.x)) * has;

  // ---- corrosion, column-locked -------------------------------------------
  // Which channels carry water. fbm1 is constant along the rib, so this is a
  // set of vertical lanes that runs the whole wall; drift wanders it slowly
  // down the tile without ever breaking the vertical wrap.
  float lane  = fbm1(uv.x * 9.0, 9.0, 3, 13.0);
  float wan   = drift(uv, 4.0, 3, 29.0);
  float wet   = sat(lane * 0.70 + wan * 0.30);
  // Water sits in the valleys and runs off the crests, so the crest only rusts
  // where a fastener has punctured it.
  float pool  = 0.18 + valley * 1.05 + web * 0.35;

  float rust = smoothstep(0.55, 0.92, wet) * pool
             + below * 0.55 + head * 0.7 + washer * 0.4;
  // The pitted front the corrosion is advancing along — gated by the lanes, not
  // summed with them, so it is structure *inside* the rusted channels rather
  // than an all-over pattern. Anisotropic, because it advances downhill.
  float bloom = ridged(uv * vec2(34.0, 20.0), vec2(34.0, 20.0), 3, 41.0);
  rust = sat(rust + (bloom - 0.5) * 0.60 * smoothstep(0.10, 0.55, rust)
                  + spEdge * smoothstep(0.30, 0.70, rust) * 0.20);

  // ---- colour --------------------------------------------------------------
  // Tonal rib banding: the crest is sun-bleached and chalky, the valley holds
  // dirt. This is the channel that survives mipmapping — at 30 m the normal map
  // has averaged to flat and *this* is what still says "corrugated".
  float zinc = 0.412
             + crest * 0.062 - valley * 0.078
             + spangle * 0.085 - spEdge * 0.045
             + (roll - 0.5) * 0.045 + (scrat - 0.5) * 0.05
             + (micro - 0.5) * 0.04;
  vec3 zincCol = vec3(zinc * 0.99, zinc, zinc * 1.025);          // zinc is slightly cool

  vec3 col = galvRamp(zincCol, rust);
  // Scaled oxide is pitted, and the pits are darker than the plates between them.
  col *= 1.0 - smoothstep(0.55, 1.0, rust) * (0.60 - bloom) * 0.50;
  col *= 1.0 - pit * smoothstep(0.25, 0.7, rust) * 0.18;
  col = mix(col, vec3(0.30, 0.30, 0.31), slot * 0.6);

  s.albedo = col;
  s.height = 0.26 + crest * 0.50
    + (oilcan - 0.5) * 0.05
    + (roll - 0.5) * 0.016
    + (scrat - 0.5) * 0.012
    + (micro - 0.5) * 0.010
    + head * 0.10 + washer * 0.05 - slot * 0.09
    - pit * 0.05 * smoothstep(0.2, 0.7, rust)
    + smoothstep(0.35, 0.70, rust) * 0.018
    - smoothstep(0.68, 1.00, rust) * 0.05;

  // The patina is a dielectric film over the zinc, not bare metal, and modelling
  // it as one is what moves the rib out of the specular channel and into the
  // diffuse one. The crest carries the thickest film — it gets the most sun and
  // the most rain — so it is the *least* metallic part of the sheet, which is
  // precisely where the blown-out band used to be.
  s.metal = (0.44 - crest * 0.12) * (1.0 - smoothstep(0.04, 0.55, rust));
  s.rough = mix(0.70 + crest * 0.07 + (1.0 - spangle) * 0.09 + (scrat - 0.5) * 0.08,
                0.94, smoothstep(0.02, 0.62, rust))
          + spEdge * 0.05 + pit * 0.06;
  s.ao    = 1.0 - valley * 0.34 - web * 0.08 - spEdge * 0.08 - slot * 0.35;
  s.grime = sat(grimeMask(uv, 191.0) * 0.55 + valley * 0.55 + smoothstep(0.3, 0.85, rust) * 0.25);
}
`,

  /* -------------------------------------------------------------- asphalt -- */
  /**
   * A road is a maintenance record. The aggregate and binder here were already
   * convincing, but a surface with no cracks, no patches, no spills and no
   * ravelling is a road that was laid this morning and never driven on, and that
   * is a stronger tell than any amount of good grain.
   *
   * What limits the answer is that the tile is **1.20 m**. That rules out every
   * damage type that is a discrete *object*: a cut repair patch, a pothole, an oil
   * spill. Authored as a region — a worley cell, or any field with a hard
   * threshold on it — each of those prints one signature blob per tile, and a road
   * with the same crescent-shaped patch every 1.2 m is far worse than a road with
   * no patches at all. (This was tried, and it looked exactly as bad as the
   * equivalent mistake documented in RUST_GLSL above.)
   *
   * What a 1.2 m tile *can* carry is damage that is genuinely fine-grained and
   * genuinely repetitive, plus damage that is **linear** — because a line drawn
   * through a periodic field joins up across tile boundaries into one continuous
   * network, and a network reads as a network however often the tile repeats:
   *
   *   fatigue   wheel paths flex and craze into a fine polygonal net
   *   ravelling stone plucked out at every crack edge; the mix opens up
   *   sealing   bitumen painted over the worst of it in fat wandering beads
   *   joints    the saw-cut edge of an old resurfacing, drawn as an *iso-contour*
   *             of a slow field rather than as a cell border, so it wanders across
   *             many tiles instead of closing into a blob inside one
   *   soiling   oil and diesel soaked into the binder — kept soft and low-contrast,
   *             because that is both what it looks like and what tiles
   */
  asphalt: /* glsl */ `
void surface(vec2 uv, out Surf s) {
  vec2 w = warp(uv, 5.0, 0.05, 17.0);

  // Aggregate is *densely packed* and coated in binder — a mosaic of stones at
  // slightly different levels, separated by grooves. Isolated round bumps read
  // as gravel scattered on a floor, which is the classic fake-asphalt mistake.
  vec3 aggA = worleyA(w * 34.0, vec2(34.0), 17.0, vec2(1.0, 1.12));
  float grooveA = 1.0 - smoothstep(0.0, 0.22, aggA.y - aggA.x);
  vec3 aggB = worleyA(uv * 78.0, vec2(78.0), 23.0, vec2(1.1, 1.0));
  float grooveB = 1.0 - smoothstep(0.0, 0.26, aggB.y - aggB.x);
  // Most of the road is flooded with binder; only worn zones expose the stone.
  float flood = smoothstep(0.22, 0.80, fbmP(uv * 6.0, 6.0, 3, 61.0));

  float binder = fbmP(uv * 12.0, 12.0, 4, 29.0);
  float grit   = fbm(uv * 300.0, 300.0, 3, 71.0);
  float micro  = fbm(uv * 620.0, 620.0, 2, 73.0);
  float polish = smoothstep(0.45, 0.85, fbmP(uv * 2.0, 2.0, 2, 143.0));  // tyre-worn

  // One warp shared by everything below. Separate ones cost 6 octaves of Perlin
  // each and took the 12-surface bake from 93 ms to 338 ms; the features are at
  // different frequencies anyway, so they do not visibly correlate through it.
  vec2 wr = warp(uv, 3.0, 0.16, 87.0);
  // Slow field, reused for resurfacing, fatigue and soiling — not a cheat: all
  // three follow from where the traffic runs. It is only ever read *softly* or as
  // a contour, never thresholded into a region.
  float region = fbmP(uv * 1.7, 1.7, 3, 151.0);

  // ---- old resurfacing joint -----------------------------------------------
  // The saw-cut edge where a lane was relaid. Drawn as an iso-contour of 'region'
  // rather than as a cell border: a contour is a *line*, so it joins up across
  // tiles into one continuous run, where a cell closes into a blob inside each.
  float resurf = smoothstep(0.44, 0.62, region);         // soft, tonal only
  float joint  = smoothstep(0.016, 0.0, abs(region - 0.53));

  // ---- fatigue cracking ----------------------------------------------------
  // Alligator cracking: a fine polygonal net, heaviest where the road flexes.
  float fatigue = smoothstep(0.48, 0.80, region);
  vec3 fw = worleyA(wr * 13.0, vec2(13.0), 163.0, vec2(1.0, 1.25));
  float alligator = (1.0 - smoothstep(0.0, 0.030, fw.y - fw.x)) * fatigue;

  vec3 cw = worley(wr * 5.0, 5.0, 43.0);
  float longCrack = (1.0 - smoothstep(0.0, 0.014, cw.y - cw.x))
                  * smoothstep(0.50, 0.80, 1.0 - region);
  float crack = sat(longCrack + alligator * 0.85);

  // Ravelling: stone plucked out along every crack edge, so the mix opens up and
  // the aggregate stands proud instead of sitting under the binder film.
  float ravel = sat((alligator * 0.6 + longCrack * 0.5) * 1.3
                    + fatigue * smoothstep(0.55, 0.85, aggA.z) * 0.45);

  // Crack sealant: bitumen painted over the worst of it in a fat wandering bead,
  // proud of the surface and much glossier than the road around it. Linear, so it
  // tiles as a network.
  float seal = sat((1.0 - smoothstep(0.0, 0.030, cw.y - cw.x))
                   * smoothstep(0.34, 0.66, region) * 1.2
                 + joint * 0.7);

  // ---- oil and diesel ------------------------------------------------------
  // Soaked into the binder rather than sitting on it. Deliberately soft and
  // low-contrast: a hard region gate here printed one identical stain per tile,
  // and a soaked-in spill genuinely has no edge to speak of anyway.
  float oilS = fbmP(wr * 9.0, 9.0, 3, 181.0);
  float oil  = smoothstep(0.52, 0.86, oilS) * smoothstep(0.40, 0.70, region);

  grooveA *= 1.0 - flood * 0.75;
  grooveB *= 1.0 - flood * 0.85;
  grooveA = sat(grooveA + ravel * 0.55);

  // The binder film covers everything, so stone shows only as a slight shift in
  // tone — never as a bright speckle.
  float tar = 0.195 + binder * 0.038 + (grit - 0.5) * 0.020 + (micro - 0.5) * 0.012;
  float c = tar * (0.94 + aggA.z * 0.13 * (1.0 - flood)) * (0.97 + aggB.z * 0.06);
  // Relaid lanes weather lighter than the mix they were cut into, so the two sides
  // of a joint never quite match. Soft, because the *edge* is the joint line — the
  // area either side of it is just a different age of asphalt.
  c = mix(c, c * 1.13, resurf * 0.6);
  c *= 1.0 - grooveA * 0.08 - crack * 0.25 - joint * 0.26;
  c *= 1.0 - oil * 0.26;
  c = mix(c, c * 0.66, seal * 0.8);               // fresh bitumen is near-black
  c *= 1.0 + ravel * 0.10;                        // exposed stone is paler than binder

  // Asphalt genuinely does skew a touch blue in daylight, but only a touch: the
  // CPU sampler this was ported from used exactly b = c * 1.04 with a neutral
  // red. The port had drifted to 0.99/1.00/1.05, which is a 6% sRGB spread —
  // ~15% once it is decoded to linear, and enough to read as a cast.
  s.albedo = vec3(c, c, c * 1.04);
  s.height = 0.52
    + (aggA.z - 0.5) * 0.10 * (1.0 - flood * 0.7) + (aggB.z - 0.5) * 0.04
    - grooveA * 0.09 - grooveB * 0.04
    + (binder - 0.5) * 0.10 + (grit - 0.5) * 0.075 + (micro - 0.5) * 0.03
    + resurf * 0.012 - joint * 0.085        // the joint line dips where it was cut
    + seal * 0.055                          // sealant is a raised bead
    - crack * 0.40
    - ravel * 0.10;
  // Oil and fresh sealant are the only glossy things on a road; ravelled stone is
  // the roughest. Polished wheel paths sit between.
  s.rough = sat(0.95 - polish * 0.16 + (grit - 0.5) * 0.06 - aggA.z * 0.05
              + ravel * 0.05 - oil * 0.34 - seal * 0.30);
  s.metal = 0.0;
  s.ao    = 1.0 - grooveA * 0.16 - grooveB * 0.07 - crack * 0.22 - joint * 0.18;
  s.grime = sat(fbmP(uv * 7.0, 7.0, 3, 199.0) * 0.7 + 0.15 + crack * 0.30 + ravel * 0.20);
}
`,

  /* ------------------------------------------------------ dry cracked dirt - */
  dirt: /* glsl */ `
void surface(vec2 uv, out Surf s) {
  vec2 w = warp(uv, 4.0, 0.08, 101.0);

  float dune  = fbmP(w * 5.0, 5.0, 5, 101.0);       // ground undulation
  float clod  = fbmP(w * 17.0, 17.0, 4, 129.0);     // clumps of dried mud
  float grain = fbm(uv * 240.0, 240.0, 3, 137.0);
  float micro = fbm(uv * 700.0, 700.0, 2, 331.0);

  // Dried mud plates: wide primary cracks, finer secondary set inside them.
  vec3 c1 = worley(warp(uv, 6.0, 0.10, 149.0) * 9.0, 9.0, 149.0);
  float crack1 = 1.0 - smoothstep(0.0, 0.055, c1.y - c1.x);
  vec3 c2 = worley(warp(uv, 12.0, 0.06, 157.0) * 22.0, 22.0, 157.0);
  float crack2 = (1.0 - smoothstep(0.0, 0.030, c2.y - c2.x)) * smoothstep(0.25, 0.6, c1.x);
  float crack = sat(crack1 + crack2 * 0.6);

  // Loose stones sitting on the surface.
  vec3 st = worley(uv * 40.0, 40.0, 163.0);
  float stone = smoothstep(0.20, 0.05, st.x) * step(0.72, st.z);

  float base = 0.40 + dune * 0.14 + (clod - 0.5) * 0.10 + (grain - 0.5) * 0.07;
  vec3 col = vec3(base * 1.16, base * 0.96, base * 0.70);        // dry warm earth
  col = mix(col, col * vec3(0.88, 0.90, 0.96), crack * 0.5);     // shadowed crack floor
  col = mix(col, vec3(0.52, 0.50, 0.47), stone * 0.7);           // grey pebbles

  s.albedo = col;
  s.height = 0.52 + (dune - 0.5) * 0.34 + (clod - 0.5) * 0.22
    + (grain - 0.5) * 0.16 + (micro - 0.5) * 0.06
    + stone * 0.22 - crack * 0.42;
  s.rough = 0.96 + (grain - 0.5) * 0.06 - stone * 0.12;
  s.metal = 0.0;
  s.ao    = 1.0 - crack * 0.45;
  s.grime = sat(fbmP(uv * 8.0, 8.0, 3, 421.0) * 0.8 + crack * 0.3);
}
`,

  /* ----------------------------------------------------------------- brick - */
  brick: /* glsl */ `
void surface(vec2 uv, out Surf s) {
  float ROWS = 16.0, COLS = 8.0;

  float ry = uv.y * ROWS;
  float row = floor(ry);
  float fy = ry - row;
  float rx = uv.x * COLS + mod(row, 2.0) * 0.5;      // running bond
  float col = floor(rx);
  float fx = rx - col;
  vec2 cell = vec2(col, row);
  vec2 counts = vec2(COLS, ROWS);

  // Ragged mortar line — a perfectly straight edge is the giveaway of a fake.
  float wob = (fbm(uv * vec2(90.0, 140.0), vec2(90.0, 140.0), 3, 401.0) - 0.5) * 0.055;
  float mw = 0.055;
  float inBrick = smoothstep(0.0, mw, fx + wob) * smoothstep(0.0, mw, 1.0 - fx + wob)
                * smoothstep(0.0, mw * 2.0, fy + wob) * smoothstep(0.0, mw * 2.0, 1.0 - fy + wob);

  float tone  = cellRand(cell, counts, 211.0);
  float proud = cellRand(cell, counts, 577.0);       // some bricks sit forward
  float burn  = cellRand(cell, counts, 733.0);       // over-fired darker bricks

  // Brick face: coarse clay pitting plus fine sand.
  float pit   = fbm(uv * 260.0, 260.0, 3, 233.0);
  float pore  = smoothstep(0.68, 0.95, fbm(uv * 120.0, 120.0, 2, 239.0));
  float face  = fbmP(uv * 26.0, 26.0, 3, 241.0);
  // Chipped corners: eat into the brick where the face noise is low near an edge.
  float rim   = 1.0 - inBrick;
  float chip  = sat((0.62 - fbm(uv * 55.0, 55.0, 3, 811.0)) * 3.0) * smoothstep(0.0, 0.5, rim) * 0.9;

  // Mortar: sandy, recessed, lighter and cooler than the clay.
  float sand  = fbm(uv * 200.0, 200.0, 3, 251.0);
  float mgrit = fbmP(uv * 60.0, 60.0, 3, 257.0);
  vec3 mortar = vec3(0.53, 0.525, 0.505) * (0.86 + sand * 0.22 + (mgrit - 0.5) * 0.14);

  vec3 clay = mix(vec3(0.46, 0.215, 0.155), vec3(0.36, 0.20, 0.165), burn);
  clay *= 0.86 + tone * 0.30;
  clay *= 0.94 + face * 0.14;
  clay = mix(clay, clay * vec3(1.06, 1.02, 0.95), pore * 0.5);

  float brickMask = sat(inBrick - chip);
  s.albedo = mix(mortar, clay, brickMask);
  s.height = 0.16
    + brickMask * (0.62 + proud * 0.14)
    + (pit - 0.5) * 0.10 * brickMask
    - pore * 0.10 * brickMask
    + (sand - 0.5) * 0.10 * (1.0 - brickMask)
    - chip * 0.22;
  s.rough = mix(0.95 + (sand - 0.5) * 0.06, 0.84 + (pit - 0.5) * 0.12, brickMask);
  s.metal = 0.0;
  s.ao    = 0.40 + brickMask * 0.60 - chip * 0.2;
  s.grime = sat(grimeMask(uv, 251.0) * 0.85 + (1.0 - brickMask) * 0.35);
}
`,

  /* ------------------------------------------------------------ wood plank - */
  wood: /* glsl */ `
void surface(vec2 uv, out Surf s) {
  float PLANKS = 5.0;
  float py = uv.y * PLANKS;
  float plank = floor(py);
  float fy = py - plank;
  vec2 pc = vec2(0.0, plank);
  vec2 counts = vec2(1.0, PLANKS);

  float sd  = floor(cellRand(pc, counts, 137.0) * 400.0);
  float sat0 = cellRand(pc, counts, 401.0);
  float shift = cellRand(pc, counts, 89.0);         // planks are not end-aligned

  // A knot per plank; the grain bows around it, which is the detail that
  // actually reads as wood rather than stripes. Distances are in tile units so
  // the knot stays round however tall a plank is.
  float kx = cellRand(pc, counts, 71.0);
  float ky = 0.28 + cellRand(pc, counts, 91.0) * 0.44;
  vec2 dk = vec2(uv.x - kx, (fy - ky) / PLANKS);
  dk.x -= round(dk.x);
  float kd = length(dk * vec2(0.55, 1.0));          // knots stretch along the grain
  float knot = smoothstep(0.060, 0.008, kd);
  float knotCore = smoothstep(0.024, 0.0, kd);

  // Growth rings run ALONG the board, so they vary across its width (fy) — the
  // single most important thing to get right, or the plank reads as end grain.
  float wobble = fbmP(uv * vec2(5.0, 3.0), vec2(5.0, 3.0), 3, sd);
  float ring = fy * 4.5 + shift * 4.0 + wobble * 1.05 + 0.26 / (kd * 6.0 + 0.15);
  float band = sin(ring * 3.14159265) * 0.5 + 0.5;
  // Earlywood is wide and pale, latewood is a narrow dark line — that contrast
  // is what makes a plank read as sawn timber rather than a beige stripe.
  float late  = smoothstep(0.28, 0.88, 1.0 - band);
  float early = smoothstep(0.30, 0.85, band);
  float rings = late;

  // Fibre striations also run along the length: fine across, coarse along.
  float grain = fbm(uv * vec2(22.0, 240.0), vec2(22.0, 240.0), 3, sd + 17.0);
  float fibre = fbm(uv * vec2(45.0, 620.0), vec2(45.0, 620.0), 2, sd + 61.0);
  float saw   = fbm(uv * vec2(260.0, 14.0), vec2(260.0, 14.0), 2, sd + 97.0);  // mill marks

  // Plank gap + the bevel that always forms along a board edge.
  float gap   = 1.0 - smoothstep(0.0, 0.020, fy) * smoothstep(0.0, 0.020, 1.0 - fy);
  float bevel = 1.0 - smoothstep(0.020, 0.070, fy) * smoothstep(0.020, 0.070, 1.0 - fy);

  // Nail heads, two per plank.
  vec2 np = vec2(fract(uv.x * 3.0 + 0.5) - 0.5, fy - 0.5);
  float nd = length(np * vec2(1.0 / 3.0, 1.0 / PLANKS));
  float nail = smoothstep(0.0075, 0.0045, nd);

  float tone = 0.285 + sat0 * 0.145;
  float lum = tone + early * 0.05 - late * 0.13 + (grain - 0.5) * 0.10 + (fibre - 0.5) * 0.05
            - knot * 0.09;
  vec3 col = vec3(lum * 1.30, lum * 0.92, lum * 0.60);
  col = mix(col, vec3(0.235, 0.155, 0.095), knot * 0.55);
  col = mix(col, vec3(0.20, 0.13, 0.085), knotCore * 0.85);
  col *= 1.0 - gap * 0.55;
  col = mix(col, vec3(0.34, 0.335, 0.33), nail * 0.85);

  s.albedo = col;
  s.height = 0.66
    - late * 0.14 + (grain - 0.5) * 0.14 + (fibre - 0.5) * 0.05
    + (saw - 0.5) * 0.04
    - gap * 0.62 - bevel * 0.10
    - knot * 0.08 + knotCore * 0.14
    + nail * 0.10;
  s.rough = 0.80 + late * 0.09 + knot * 0.10 - nail * 0.35 + (fibre - 0.5) * 0.08;
  s.metal = nail * 0.9;
  s.ao    = 1.0 - gap * 0.75 - bevel * 0.15 - knot * 0.2;
  s.grime = sat(grimeMask(uv, 619.0) * 0.8 + gap * 0.5);
}
`,

  /* --------------------------------------------- gun metal (hero material) - */
  /**
   * Why this had "no three-dimensional read".
   *
   * The height field was essentially flat (measured mean normal slope 0.067,
   * about 4 degrees) and what little relief it had was the bead-blast cell
   * pattern — isotropic dots at 230 cycles a tile. The cavity/curvature system in
   * pass 2 can only answer with what it is given: fed isotropic dots it produces
   * an isotropic speckle, so `wear` painted bright steel flecks evenly over every
   * face and edge alike. Faces and edges then carried the same value because the
   * material had no idea which was which.
   *
   * The fix that matters is **specular definition, not texture content**. No
   * texture can know where `chamferBox` put its break-edges — that is geometry,
   * and the material never sees it. What the material does control is whether a
   * facet tilted 45 degrees away from its neighbour *looks* different, and at
   * roughness 0.58 it does not: the lobe is broad enough that the chamfer and the
   * face integrate almost the same incoming light, so they come out the same
   * value. Dropping the phosphate to 0.47, widening the spread between porous
   * coating and burnished high points, and raising `envIntensity` so a tilted
   * facet samples a genuinely different part of the environment is what makes a
   * 1 mm chamfer draw a line of light.
   *
   * A cellular "milled facet" pattern was tried here and removed. At the
   * viewmodel's 0.055 m texel density any worley structure lands at 6-9 mm, and a
   * worley border set is a closed polygonal net — which on a hand prop reads
   * unambiguously as crazed paint or dried mud, never as machining. Real milling
   * relief is *directional*: long straight cuts running with the bore. So the
   * only structure added is anisotropic — tool scallops and cutter marks along u
   * — and the handling wear is stretched the same way, because a rifle wears in
   * streaks along its length, not in round patches.
   */
  gunmetal: /* glsl */ `
void surface(vec2 uv, out Surf s) {
  // Bead-blasted, phosphated steel. The blast pattern is a *roughness* feature —
  // it is what makes the finish matte — so it is deliberately kept out of the
  // height field, where it would only feed the curvature system noise.
  vec3 bead   = worley(uv * 230.0, 230.0, 331.0);
  float dim   = smoothstep(0.50, 0.05, bead.x);
  float micro = fbm(uv * 520.0, 520.0, 2, 313.0);
  float mach  = fbm(uv * vec2(620.0, 7.0), vec2(620.0, 7.0), 2, 359.0);   // tool marks
  float meso  = fbmP(uv * 8.0, 8.0, 3, 347.0);
  float grain0 = fbmP(uv * 20.0, 20.0, 3, 353.0);

  // ---- machined structure --------------------------------------------------
  // Milling relief runs *with* the bore: long straight cuts, and the cutter's
  // scallop pattern across each one. Both are strongly anisotropic and both are
  // shallow — this is on the order of 0.1 mm of relief on a real receiver.
  float cut  = fbm(uv * vec2(11.0, 2.0), vec2(11.0, 2.0), 2, 371.0);   // milled reliefs
  float tool = fbm(uv * vec2(210.0, 4.0), vec2(210.0, 4.0), 2, 379.0); // cutter scallops

  // Handling wear. Stretched hard along u, because a rifle loses its finish in
  // streaks down its length and along the edges it is set down on — not in round
  // patches, which is what a symmetric fbm gives and what read as spatter.
  float wearN = sat((fbmP(uv * vec2(26.0, 7.0), vec2(26.0, 7.0), 4, 349.0) - 0.605) * 6.5);
  float scuff = fbm(uv * vec2(900.0, 26.0), vec2(900.0, 26.0), 2, 367.0);
  float bare  = sat(wearN * 0.70 + (scuff - 0.5) * 0.6 * wearN);

  // Manganese phosphate is the exception to the "metals are bright" rule at the
  // top of this file. It is not bare steel with a rough finish — it is a porous
  // conversion coating that genuinely absorbs, and a service rifle reads as
  // near-black in the hand. 0.255 in this perceptual space is 0.052 linear.
  float base = 0.255 + dim * 0.013 + (micro - 0.5) * 0.020 + (meso - 0.5) * 0.024
             + (mach - 0.5) * 0.014 + (cut - 0.5) * 0.018;
  vec3 col = vec3(base * 1.03, base, base * 0.96);            // phosphate reads warm
  col = mix(col, vec3(0.68, 0.685, 0.695), bare);             // polished bare steel

  s.albedo = col;
  // Relief stays shallow and directional. The face needs an *interior* — some
  // reason for one part of a flat to be brighter than another — without ever
  // acquiring a pattern of its own; anything with a recognisable shape at this
  // texel density reads as damage.
  s.height = 0.52
           + (cut  - 0.5) * 0.100          // milled reliefs along the bore
           + (tool - 0.5) * 0.032          // cutter scallops across them
           + (micro - 0.5) * 0.020
           + (mach - 0.5) * 0.018
           + (meso - 0.5) * 0.075
           + (grain0 - 0.5) * 0.038;
  s.metal = 1.0;
  // Phosphate is matte but not featureless, and the spread between the porous
  // coating and the places it has been burnished smooth is what gives a face an
  // interior. Bare steel is properly glossy.
  s.rough = mix(0.47 + dim * 0.15 + (micro - 0.5) * 0.07 + (grain0 - 0.5) * 0.06, 0.13, bare)
          + (tool - 0.5) * 0.07 + (mach - 0.5) * 0.04;
  s.ao    = 1.0 - dim * 0.06;
  s.grime = sat(fbmP(uv * 9.0, 9.0, 3, 383.0) * 0.6);
}
`,

  /* ------------------------------------------------- polymer furniture ----- */
  /**
   * Feature sizes here are set by the *weapon's* texel density, which is
   * `DEFAULT_UV_SCALE` in the viewmodel primitives: **0.055 m per tile**. (The
   * 0.35 m figure in `world/Geometry.js` is the world-prop density — crates and
   * drums — and is an order of magnitude coarser on purpose.) So a feature
   * authored at N cycles per tile lands at 55/N mm on a stock or grip:
   *
   *   stipple  52/tile -> 1.06 mm    the moulded-in tool texture
   *   grain    46/tile -> 1.20 mm
   *   fibre   150/tile -> 0.37 mm    glass reinforcement showing through
   *
   * The previous 18-cycle "mould" band was 3.1 mm and carried the largest single
   * albedo modulation on the surface, which is why a 25 cm stock read as a lump
   * of cast plaster: it only had eighty features across it, and they were the
   * loudest thing on the part.
   *
   * Colour is FDE, not neutral. That is not a stylistic preference — a neutral
   * grey is the one thing that cannot survive a coloured key light, because it
   * has no hue of its own to hold against it. Under this level's low warm sun a
   * neutral 0.10 albedo renders as salmon pink; an earth tone renders as an
   * earth tone. The value also comes down: the old 0.103 linear was raised from
   * 0.033 on the grounds that a caller can only darken with a colour tint, but
   * every glove material in `weapons/models/hands.js` passes `map: null` and
   * drives colour directly, so nothing is relying on the map being light.
   */
  polymer: /* glsl */ `
void surface(vec2 uv, out Surf s) {
  // Injection-moulded glass-filled nylon. The moulded-in tool texture is a fine
  // even stipple — roughly 1 mm cells — not sandpaper and not plaster.
  vec3 stip = worleyA(warp(uv, 16.0, 0.02, 409.0) * 52.0, vec2(52.0), 401.0, vec2(1.0, 1.18));
  float dot0 = smoothstep(0.44, 0.10, stip.x);
  float dotEdge = 1.0 - smoothstep(0.0, 0.10, stip.y - stip.x);
  // Fine moulding grain between the stipple cells, and the polish variation the
  // tool itself leaves. Both an octave up from where they were.
  float grain = fbmP(uv * 46.0, 46.0, 3, 419.0);
  float micro = fbm(uv * 430.0, 430.0, 2, 421.0);
  float fill  = smoothstep(0.74, 0.96, fbm(uv * 150.0, 150.0, 2, 431.0));  // glass fibre

  // Mould flow lines and a parting seam. Flow is a gloss artefact, not a colour
  // one — it is where the melt front cooled at a different rate, so it shows as a
  // change in sheen. Putting it in the albedo was half of the "plaster" look.
  float flow = fbmP(uv * vec2(6.0, 40.0), vec2(6.0, 40.0), 3, 433.0);
  float seam = smoothstep(0.010, 0.0, abs(fract(uv.y + 0.5) - 0.5));

  // Handling scuffs: abraded nylon goes chalky and slightly *lighter*, so this is
  // the one thing allowed to brighten the surface — sparingly.
  float scuff = sat((fbmP(uv * 26.0, 26.0, 3, 439.0) - 0.62) * 3.2);

  // Flat dark earth, normalised on red. Ratios rather than a hex so the value and
  // the hue stay independently tunable.
  const vec3 FDE = vec3(1.00, 0.865, 0.625);

  float base = 0.345 + grain * 0.022 + dot0 * 0.016 + (micro - 0.5) * 0.014
             + fill * 0.030;
  vec3 col = vec3(base) * FDE;
  col = mix(col, col * vec3(1.06, 1.08, 1.14), fill * 0.5);   // fibre reads cooler
  col += scuff * 0.038 * vec3(1.0, 0.96, 0.88);

  s.albedo = col;
  s.height = 0.50 + dot0 * 0.17 - dotEdge * 0.14 + (grain - 0.5) * 0.06
           + (micro - 0.5) * 0.022 + seam * 0.10;
  s.metal = 0.0;
  s.rough = 0.72 - dot0 * 0.08 + dotEdge * 0.06 + scuff * 0.12
          + (flow - 0.5) * 0.09 + (micro - 0.5) * 0.04 - fill * 0.10;
  s.ao    = 1.0 - dotEdge * 0.20;
  s.grime = sat(fbmP(uv * 13.0, 13.0, 3, 443.0) * 0.55);
}
`,

  /* ------------------------------------------------------------- glass ----- */
  glass: /* glsl */ `
void surface(vec2 uv, out Surf s) {
  // Dirty tinted window glass. Almost flat, almost black, very smooth — what
  // sells it is the reflection, so the job here is roughness variation.
  float dust  = fbmP(uv * 40.0, 40.0, 4, 501.0);
  float haze  = fbmP(uv * 6.0, 6.0, 3, 509.0);
  float smear = streak(uv * vec2(14.0, 9.0), vec2(14.0, 9.0), 517.0, 10);
  float wipe  = fbmP(uv * vec2(30.0, 4.0), vec2(30.0, 4.0), 2, 523.0);
  float specks = smoothstep(0.86, 0.98, fbm(uv * 190.0, 190.0, 2, 541.0));
  float wave  = fbmP(uv * 3.0, 3.0, 2, 547.0);        // float-glass waviness

  float grime = sat(dust * 0.5 + haze * 0.3 + smear * 0.4 - 0.35);

  float tint = 0.225 + haze * 0.03 + grime * 0.055 + specks * 0.10;
  s.albedo = vec3(tint * 0.94, tint, tint * 0.97);    // faint green float-glass tint
  s.height = 0.5 + (wave - 0.5) * 0.10 + grime * 0.03 + specks * 0.06;
  s.metal = 0.0;
  s.rough = sat(0.035 + grime * 0.22 + wipe * 0.05 + specks * 0.35);
  s.ao    = 1.0;
  s.grime = sat(grime * 1.4);
}
`,

  /* ----------------------------------------------------- sandbag / burlap -- */
  fabric: /* glsl */ `
void surface(vec2 uv, out Surf s) {
  float N = 26.0;                       // threads per tile, each way
  vec2 t = uv * N;
  vec2 cell = floor(t);
  float over = mod(cell.x + cell.y, 2.0);   // plain weave: alternating over/under

  float pw = sin(uv.x * 6.28318530718 * N) * 0.5 + 0.5;   // warp thread profile
  float pf = sin(uv.y * 6.28318530718 * N) * 0.5 + 0.5;   // weft thread profile
  float topT = mix(pf, pw, over);
  float botT = mix(pw, pf, over);

  // Individual fibres twisting along each thread + loose fuzz.
  float twistW = fbm(uv * vec2(N * 1.0, N * 6.0), vec2(N, N * 6.0), 2, 601.0);
  float twistF = fbm(uv * vec2(N * 6.0, N * 1.0), vec2(N * 6.0, N), 2, 607.0);
  float twist = mix(twistF, twistW, over);
  float fuzz = fbm(uv * 420.0, 420.0, 3, 613.0);

  // Hand-spun jute is not uniform: every thread has its own thickness.
  float thW = 0.80 + cellRand(vec2(cell.x, 0.0), vec2(N, 1.0), 631.0) * 0.40;
  float thF = 0.80 + cellRand(vec2(0.0, cell.y), vec2(1.0, N), 641.0) * 0.40;
  float thick = mix(thF, thW, over);
  topT *= thick;
  botT *= mix(thW, thF, over);

  float weave = topT * 0.78 + botT * 0.18 + (twist - 0.5) * 0.22;

  float dirtN = fbmP(uv * 7.0, 7.0, 4, 617.0);
  float stain = grimeMask(uv, 619.0);

  float base = 0.40 + dirtN * 0.13 + (fuzz - 0.5) * 0.05 + (twist - 0.5) * 0.08;
  vec3 col = vec3(base * 1.12, base * 1.00, base * 0.72);       // hessian tan
  col = mix(col, col * vec3(0.86, 0.85, 0.82), stain * 0.5);

  s.albedo = col;
  s.height = sat(0.20 + weave * 0.72 + (fuzz - 0.5) * 0.10);
  s.rough  = 0.95 + (fuzz - 0.5) * 0.06 - topT * 0.05;
  s.metal  = 0.0;
  s.ao     = 0.58 + topT * 0.32 + botT * 0.10;
  s.grime  = sat(stain * 0.9 + 0.15);
}
`,

  /* ------------------------------------------------------- ceramic tile ---- */
  tile: /* glsl */ `
void surface(vec2 uv, out Surf s) {
  float N = 4.0;
  vec2 t = uv * N;
  vec2 cell = floor(t);
  vec2 f = t - cell;
  vec2 counts = vec2(N);

  float wob = (fbm(uv * 150.0, 150.0, 3, 701.0) - 0.5) * 0.035;
  float g = 0.085;
  float inTile = smoothstep(0.0, g, f.x + wob) * smoothstep(0.0, g, 1.0 - f.x + wob)
               * smoothstep(0.0, g, f.y + wob) * smoothstep(0.0, g, 1.0 - f.y + wob);

  float tint  = cellRand(cell, counts, 701.0);
  float shade = cellRand(cell, counts, 907.0);
  float lay   = cellRand(cell, counts, 913.0);       // tiny per-tile height offset

  // Glaze: subtle orange-peel waviness and a few firing specks.
  float glaze = fbmP(uv * 40.0, 40.0, 3, 719.0);
  float speck = smoothstep(0.88, 0.98, fbm(uv * 220.0, 220.0, 2, 727.0));
  vec3 cz = worley(warp(uv, 10.0, 0.05, 731.0) * 26.0, 26.0, 733.0);
  float crack = (1.0 - smoothstep(0.0, 0.008, cz.y - cz.x))
              * smoothstep(0.55, 0.85, fbmP(uv * 5.0, 5.0, 3, 739.0));   // crazing

  // Chipped glaze at the tile corners exposes the raw biscuit.
  float rim = 1.0 - inTile;
  float chip = sat((0.60 - fbm(uv * 60.0, 60.0, 3, 743.0)) * 3.2) * smoothstep(0.0, 0.55, rim) * 0.85;

  // Grout: coarse, porous, always dirtier than the tile.
  float sand  = fbm(uv * 230.0, 230.0, 3, 751.0);
  float gmeso = fbmP(uv * 55.0, 55.0, 3, 757.0);
  vec3 grout = vec3(0.408, 0.395, 0.370) * (0.84 + sand * 0.26 + (gmeso - 0.5) * 0.16);

  // Institutional tile. This was the brightest thing on screen — measured at 0.28
  // linear mean with 72% of its texels below roughness 0.31, which is a mirror
  // finish on a light surface, so in an interior it was integrating most of the
  // sky through its specular and reading at ~0.9 in shade. Both halves of that
  // are wrong for a floor in a derelict building: forty years of foot traffic
  // takes the gloss off ceramic long before it takes the colour out, and the
  // colour was never white to begin with.
  vec3 glazeCol = mix(vec3(0.505, 0.500, 0.474), vec3(0.408, 0.440, 0.440), tint);
  glazeCol = mix(glazeCol, vec3(0.478, 0.458, 0.400), step(0.78, shade));  // odd sandy tile
  glazeCol *= 0.90 + shade * 0.14 + glaze * 0.06;
  glazeCol = mix(glazeCol, vec3(0.395, 0.352, 0.310), chip * 0.8);   // raw biscuit

  float mask = sat(inTile - chip * 0.6);
  s.albedo = mix(grout, glazeCol, mask);
  s.height = 0.14 + mask * (0.66 + lay * 0.06)
    + (glaze - 0.5) * 0.05 * mask
    + (sand - 0.5) * 0.12 * (1.0 - mask)
    - chip * 0.20 - crack * 0.08 * mask;
  // Scuffed glaze, not showroom glaze: the traffic lanes are the dullest part.
  s.rough = mix(0.94 + (sand - 0.5) * 0.06,
                0.44 + glaze * 0.12 + speck * 0.20 + chip * 0.40 - shade * 0.06, mask);
  s.metal = 0.0;
  s.ao    = 0.42 + mask * 0.58 - chip * 0.15;
  s.grime = sat(grimeMask(uv, 727.0) * 0.75 + (1.0 - mask) * 0.45);
}
`,
};
